/**
 * Role permission utilities for user management
 */

/**
 * Assignable roles for user role selection in organization context
 * (OrganizationDetailPage Users tab - excludes managed_tenant_user)
 */
export const ORGANIZATION_ASSIGNABLE_ROLES = [
  { value: 'global_admin', label: 'Global Admin' },
  { value: 'organization_owner', label: 'Org Owner' },
  { value: 'organization_admin', label: 'Org Admin' },
  { value: 'organization_user', label: 'Org User' },
];

/**
 * Assignable roles for user role selection in tenant context
 * (regular Users page - includes managed_tenant_user)
 */
export const TENANT_ASSIGNABLE_ROLES = [
  { value: 'global_admin', label: 'Global Admin' },
  { value: 'organization_owner', label: 'Org Owner' },
  { value: 'organization_admin', label: 'Org Admin' },
  { value: 'organization_user', label: 'Org User' },
  { value: 'managed_tenant_user', label: 'Tenant User' },
];

/**
 * Roles assignable by organization owners in organization context
 */
export const ORG_OWNER_ORGANIZATION_ASSIGNABLE_ROLES = [
  { value: 'organization_owner', label: 'Org Owner' },
  { value: 'organization_admin', label: 'Org Admin' },
  { value: 'organization_user', label: 'Org User' },
];

/**
 * Roles assignable by organization owners in tenant context
 * (includes managed_tenant_user)
 */
export const ORG_OWNER_TENANT_ASSIGNABLE_ROLES = [
  { value: 'organization_owner', label: 'Org Owner' },
  { value: 'organization_admin', label: 'Org Admin' },
  { value: 'organization_user', label: 'Org User' },
  { value: 'managed_tenant_user', label: 'Tenant User' },
];

/**
 * Determine if current user can edit the target user's role
 * @param currentUserRole - The role of the user viewing/editing
 * @param targetUserRole - The role of the user being edited
 * @returns true if role can be edited
 */
export const canEditRole = (currentUserRole: string | undefined, targetUserRole: string | undefined): boolean => {
  if (currentUserRole === 'global_admin') {
    return true;
  }
  // Org owners can edit all users except global_admins
  if (currentUserRole === 'organization_owner' && targetUserRole !== 'global_admin') {
    return true;
  }
  return false;
};

/**
 * Get the list of assignable roles based on current user's role and context
 * @param currentUserRole - The role of the current user
 * @param isOrganizationContext - True if in organization context (excludes managed_tenant_user),
 *                                 false for tenant context (includes managed_tenant_user).
 *                                 Defaults to true (organization context).
 */
export const getAssignableRoles = (
  currentUserRole: string | undefined,
  isOrganizationContext: boolean = true
) => {
  if (currentUserRole === 'global_admin') {
    return isOrganizationContext ? ORGANIZATION_ASSIGNABLE_ROLES : TENANT_ASSIGNABLE_ROLES;
  }
  if (currentUserRole === 'organization_owner') {
    return isOrganizationContext ? ORG_OWNER_ORGANIZATION_ASSIGNABLE_ROLES : ORG_OWNER_TENANT_ASSIGNABLE_ROLES;
  }
  return [];
};

/**
 * Get display text for a role value
 */
export const getRoleDisplayText = (role: string | null | undefined): string => {
  switch (role) {
    case 'global_admin':
      return 'Global Admin';
    case 'organization_owner':
      return 'Org Owner';
    case 'organization_admin':
      return 'Org Admin';
    case 'organization_user':
      return 'Org User';
    case 'managed_tenant_user':
      return 'Tenant User';
    default:
      return 'User';
  }
};
